/* CartPerformance — required by Dawn's product-form.js, cart.js and
   cart-drawer.js, which all call it as a bare global. It was missing from this
   theme entirely, so add-to-cart threw a ReferenceError before the drawer
   could render.

   Everything here is wrapped in an IIFE and guarded. A top-level `class`
   declaration creates a global lexical binding, so if this file is evaluated
   twice (theme-editor re-render, CLI hot reload, a duplicated script tag) the
   second pass dies with "Identifier 'CartPerformance' has already been
   declared" — taking the shim and fail-safe below down with it. Re-running
   this file is now a no-op instead. */
(function () {
  'use strict';

  if (!window.CartPerformance) {
    var METRIC_PREFIX = 'cart-performance';

    function createUUID() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        try {
          return window.crypto.randomUUID();
        } catch (e) {
          /* fall through */
        }
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        var v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }

    function record(benchmarkName, startMarker) {
      if (!startMarker) return;
      try {
        performance.measure(METRIC_PREFIX + ':' + benchmarkName, {
          start: startMarker.startTime,
          end: performance.now()
        });
      } catch (e) {
        /* measurement is diagnostic only — never let it break the cart */
      }
    }

    var CartPerformance = {
      createUUID: createUUID,

      createStartingMarker: function (benchmarkName) {
        var name = benchmarkName + '-start-' + createUUID();
        try {
          return performance.mark(name);
        } catch (e) {
          return { name: name, startTime: performance.now() };
        }
      },

      measureFromEvent: function (benchmarkName, event) {
        var startMarker = CartPerformance.createStartingMarker(benchmarkName);
        try {
          if (startMarker && event && typeof event.timeStamp === 'number') {
            startMarker.startTime = event.timeStamp;
          }
        } catch (e) {
          /* PerformanceMark.startTime is read-only in some browsers */
        }
        record(benchmarkName, startMarker);
      },

      measureFromMarker: function (benchmarkName, startMarker) {
        record(benchmarkName, startMarker);
      },

      measure: function (benchmarkName, callback) {
        var startMarker = CartPerformance.createStartingMarker(benchmarkName);
        var result;
        try {
          result = callback();
        } catch (err) {
          record(benchmarkName, startMarker);
          console.error('[AVELIQ cart] render step threw:', err);
          return undefined;
        }
        if (result && typeof result.then === 'function') {
          return result.then(
            function (value) { record(benchmarkName, startMarker); return value; },
            function (err) { record(benchmarkName, startMarker); throw err; }
          );
        }
        record(benchmarkName, startMarker);
        return result;
      }
    };

    window.CartPerformance = CartPerformance;
  }

  if (window.__aqCartBootstrapped) return;
  window.__aqCartBootstrapped = true;

  /* -----------------------------------------------------------------------
     Shim: ProductInfo#fetchQuantityRules

     Dawn subscribes this method to cartUpdate whenever a section renders a
     .quantity__input inside .product-form__quantity — which the AVELIQ product
     section does. It then assumes Dawn's quantity-rules markup is present too,
     and dereferences it unguarded:

         this.querySelector('.quantity__rules-cart .loading__spinner').classList

     Our custom buy box has no volume-pricing / quantity-rules UI, so that is
     null and the method throws on every cart update. pubsub.js now isolates
     subscribers so this can't break the cart, but the method has nothing to do
     here — make it a clean no-op rather than a caught exception each time.
     ----------------------------------------------------------------------- */
  function shimQuantityRules() {
    var ProductInfo = window.customElements && customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.__aqQtyShim) return;
    ProductInfo.prototype.__aqQtyShim = true;

    var original = ProductInfo.prototype.fetchQuantityRules;
    ProductInfo.prototype.fetchQuantityRules = function () {
      if (!this.querySelector('.quantity__rules-cart .loading__spinner')) return;
      return original.apply(this, arguments);
    };
  }

  /* -----------------------------------------------------------------------
     Drawer fail-safe. product-form.js publishes cartUpdate (carrying the full
     payload, rendered sections included) before calling renderContents. If
     Dawn's render path fails, the item is in the cart but the drawer never
     opens. 350ms after each add, if the drawer still isn't open, paint it from
     that payload and open it. No-ops when Dawn succeeds.
     ----------------------------------------------------------------------- */
  function wireFailSafe() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      setTimeout(function () {
        var drawer = document.querySelector('cart-drawer');
        if (!drawer || drawer.classList.contains('active')) return; /* Dawn succeeded */
        console.warn('[AVELIQ cart] drawer did not open via Dawn path — fail-safe engaging');
        try {
          var sections = event.cartData && event.cartData.sections;
          if (sections && sections['cart-drawer']) {
            var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            var src = doc.querySelector('#CartDrawer');
            var dst = document.querySelector('#CartDrawer');
            if (src && dst) dst.innerHTML = src.innerHTML;
            var inner = drawer.querySelector('.drawer__inner');
            if (inner) inner.classList.remove('is-empty');
            drawer.classList.remove('is-empty');
          }
          if (sections && sections['cart-icon-bubble']) {
            var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            var bsrc = bdoc.querySelector('.shopify-section');
            var bdst = document.getElementById('cart-icon-bubble');
            if (bsrc && bdst) bdst.innerHTML = bsrc.innerHTML;
          }
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay && !overlay.dataset.aqBound) {
            overlay.dataset.aqBound = '1';
            overlay.addEventListener('click', function () {
              if (typeof drawer.close === 'function') drawer.close();
              else {
                drawer.classList.remove('active');
                document.body.classList.remove('overflow-hidden');
              }
            });
          }
          if (typeof drawer.open === 'function') {
            drawer.open();
          } else {
            drawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        } catch (err) {
          console.error('[AVELIQ cart] fail-safe could not render the drawer:', err);
        }
      }, 350);
    });
  }

  function boot() {
    shimQuantityRules();
    wireFailSafe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
